/* eslint-disable react/display-name */
import dayjs from "dayjs";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlinePlaylistPlay } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import { ImageData } from "~/interface/commonImagesize-types";
import { NodeType } from "~/interface/video-category/type";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { cn } from "~/lib/utils";
import { BLUR_HASH, COLORS, FALLBACK_IMAGE_ALT_TEXT, FALLBACK_IMAGE_URL } from "~/utils/constant";
import { convertSecondsToHMS, mediaAudioOnly } from "~/utils/helper-funcations";
import AudioIcon from "../shared-components/AudioIcon";
import ClientOnly from "../shared-components/ClentOnly";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import Tag from "../shared-components/Tag";
import { DISPLAY_TYPE } from "../video-grid/video-data";
import CardShadow from "./CardShadow";

export type PropsType = {
  className?: string;
  data: {
    video_id: string | null;
    name: string;
    record_date?: string;
    duration_sec?: number;
    node_type?: NodeType;
    img: ImageData | null;
    part?: number;
    tag?: string | null;
    vertical_img?: ImageData | null;
    square_img: ImageData | null;
    media_type?: string | null;
    playlist_item_count?: number | null;
  };
};

function RectangularCard({ className, data }: PropsType) {
  const isPlaylist = data?.node_type ? data?.node_type !== DISPLAY_TYPE.branch : false;
  const isBranch = data?.node_type ? data?.node_type == DISPLAY_TYPE.branch : false;
  const isAudioOnly = mediaAudioOnly(data?.media_type!);
  const { normalInter } = useFontStyle();
  const { t } = useTranslation();

  const ImageCard = memo(() => (
    <ImageWithSkeleton
      url={{
        small: data?.img?.s?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "low")) || FALLBACK_IMAGE_URL,
        medium: data?.img?.m?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) || FALLBACK_IMAGE_URL,
        large: data?.img?.l?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) || FALLBACK_IMAGE_URL,
        hd: data?.img?.hd?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "high")) || FALLBACK_IMAGE_URL,
        original: data?.img?.og?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "maximum")) || FALLBACK_IMAGE_URL,
      }}
      alt={data?.name ?? FALLBACK_IMAGE_ALT_TEXT}
      className="w-full h-full object-cover"
      roundedClass="rounded-xl"
      blur_hash={data?.img?.bh || BLUR_HASH}
      isPreloaded={true}
    />
  ));

  return (
    <div className={`w-full flex flex-col gap-1 justify-center items-center cursor-pointer relative group ${className}`}>
      {/* Thumbnail Section */}
      {isPlaylist && <CardShadow bgImage={data?.img?.og?.url || FALLBACK_IMAGE_URL} />}

      {/* Main Thumbnail */}
      <div className="w-full aspect-video bg-gray-300 relative overflow-hidden z-10 rounded-xl ">
        <ClientOnly fallback={<ImageCard />}>
          <ImageCard />
        </ClientOnly>

        {data?.tag && <div className="w-auto absolute top-2 right-2">{<Tag text={data?.tag} />}</div>}

        {/* Playlist Tag */}

        {isPlaylist ? (
          <div className="absolute  bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center  bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.part} {t(`common.videos`, { defaultValue: "Videos" })}
            </span>
          </div>
        ) : data?.duration_sec ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center justify-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-10 xs:min-w-[40px] md:min-w-[40px]">
            <span className={cn(normalInter)}>{convertSecondsToHMS(data?.duration_sec || 0)}</span>
          </div>
        ) : isBranch ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center justify-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-10 xs:min-w-[40px] md:min-w-[40px]">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.playlist_item_count || 0} {t(`common.playlist`, { defaultValue: "Playlist" })}
            </span>
          </div>
        ) : null}

        {isAudioOnly && <AudioIcon isVisible={isAudioOnly} className="absolute bottom-2 left-2" />}
      </div>

      {/* Video Details */}
      <div className="w-full flex flex-col justify-start items-start ">
        <p
          className={cn(
            "common-text-size text-textColorPrimary line-clamp-2 leading-5 overflow-hidden group-hover:text-darkOrangeColor/80 transition-colors duration-200 ease-in-out w-full",
            normalInter,
          )}
          title={data?.name}
        >
          {data?.name || ""}
        </p>

        {(data?.duration_sec || data?.record_date) && (
          <div className="flex justify-start items-center gap-2">
            {data?.record_date && (
              <p className={cn("body_text_xs text-textSecondaryColor", normalInter)}>{dayjs(data?.record_date).format("DD MMM YYYY")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RectangularCard;
